import * as React from 'react'
import { cn } from '@/lib/utils'
import { Skeleton } from './skeleton'

/**
 * KPI tile — Bloomberg-terminal aesthetic.
 *
 *   ┌─────────────────────────────┐
 *   │ OPEN CASES           ↑ 4.2% │   ← label (uppercase tracking) + delta
 *   │ 1,284                       │   ← big tabular number
 *   │ vs 1,232 yesterday          │   ← context tail
 *   └─────────────────────────────┘
 *
 * Numeric value is mono-style with tabular nums.
 * Delta arrow follows the design rule: red = NEGATIVE only.
 */
export interface KpiProps {
  label: string
  value: React.ReactNode
  /** Optional context line below the value ("vs ₹1.21Cr yesterday"). */
  hint?: React.ReactNode
  /** Delta in percentage points, signed. + = improvement domain-specifically. */
  delta?: number | null
  /**
   * Semantics for the delta arrow.
   * - 'higher-is-better': cases recovered, customers paid (default for positive trends)
   * - 'lower-is-better':  open cases, overdue amount (default for negative trends)
   * - 'neutral':           pure counter, no color
   */
  deltaSense?: 'higher-is-better' | 'lower-is-better' | 'neutral'
  loading?: boolean
  className?: string
  icon?: React.ReactNode
}

export function Kpi({
  label, value, hint, delta, deltaSense = 'higher-is-better', loading, className, icon,
}: KpiProps) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-md border border-slate-200 bg-card p-4 shadow-soft',
        'transition-shadow duration-fluid ease-fluid hover:shadow-lift motion-reduce:transition-none',
        className,
      )}
    >
      {/* Subtle navy accent rail on the left */}
      <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-navy-700/70" />

      <div className="flex items-start justify-between gap-3">
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-700/80">{label}</p>
        {icon && <span className="text-navy-500">{icon}</span>}
      </div>

      <div className="mt-2.5 flex items-baseline gap-2.5">
        {loading ? (
          <Skeleton className="h-7 w-28" />
        ) : (
          <span className="font-sans text-3xl font-semibold tabular-nums leading-none text-navy-900">
            {value}
          </span>
        )}
        {delta != null && !loading && <DeltaPill delta={delta} sense={deltaSense} />}
      </div>

      {hint && !loading && (
        <p className="mt-2 truncate text-xs text-muted-foreground">{hint}</p>
      )}
      {loading && <Skeleton className="mt-2 h-3 w-24" />}
    </div>
  )
}

function DeltaPill({ delta, sense }: { delta: number; sense: KpiProps['deltaSense'] }) {
  const up = delta > 0
  const down = delta < 0
  const zero = delta === 0

  // Map to semantic colour following the design rule (red = NEGATIVE only).
  let tone: 'pos' | 'neg' | 'neutral' = 'neutral'
  if (!zero) {
    if (sense === 'neutral') tone = 'neutral'
    else if (sense === 'higher-is-better') tone = up ? 'pos' : 'neg'
    else tone = up ? 'neg' : 'pos'
  }

  const cls =
    tone === 'pos'
      ? 'bg-positive-soft text-positive border-positive/30'
      : tone === 'neg'
      ? 'bg-negative-soft text-negative border-negative/30'
      : 'bg-slate-50 text-slate-600 border-slate-200'

  const arrow = up ? '▲' : down ? '▼' : '·'
  return (
    <span className={cn('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-2xs font-medium tabular-nums', cls)}>
      <span aria-hidden>{arrow}</span>
      {Math.abs(delta).toFixed(1)}%
    </span>
  )
}
